import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import FluidSubpageHero from '../components/FluidSubpageHero';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import MedicalClinicSchema from '../components/MedicalClinicSchema';

export default function OmOs() {
  const { t, lang, getLocalizedValue } = useLanguage();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);

  const seoTitle = lang === 'da'
    ? 'Om os – Specialister i ADHD, autisme og psykiatri | Nordisk Psykiatri'
    : 'Om oss – Specialister inom ADHD, autism och psykiatri | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Mød vores tværfaglige team af erfarne speciallæger i psykiatri og psykologer. Vi tilbyder grundig udredning og evidensbaseret behandling i hjertet af København.'
    : 'Möt vårt tvärvetenskapliga team av erfarna specialistläkare i psykiatri och psykologer. Vi erbjuder noggrann utredning och evidensbaserad behandling i hjärtat av Köpenhamn.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const renderFormattedText = (content, defaultText, className = "") => {
    const text = content ? getLocalizedValue(content) : defaultText;
    if (!text) return null;
    if (typeof text === 'string' && (text.includes('<p') || text.includes('<br') || text.includes('<span') || text.includes('<div') || text.includes('<h'))) {
      return (
        <div 
          className={className} 
          dangerouslySetInnerHTML={{ __html: text }} 
        />
      );
    }
    return <p className={className}>{text}</p>;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch page content
        const pageRes = await fetch('https://api.dev.auxo.dk/public/content/om_os_side');
        if (pageRes.ok) {
          const data = await pageRes.json();
          if (Array.isArray(data) && data.length > 0) {
            setPageData(data[0]);
          }
        }
      } catch (e) {
        console.error('Error fetching om_os_side:', e);
      }

      try {
        let res = await fetch('https://api.dev.auxo.dk/public/content/behandlere');
        if (!res.ok) {
          res = await fetch('https://api.dev.auxo.dk/public/content/team');
        }
        if (res.ok) {
          const data = await res.json();
          setTeam(data || []);
        }
      } catch (error) {
        console.error('Error fetching team from CMS:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-sand-light">
      <MedicalClinicSchema />
      {/* Hero & Team Section (Erstatning af FluidSubpageHero og tilføjelse af de to team sektioner) */}
      <section className="relative overflow-hidden bg-[#fbfaf7] text-[#163f3a] pt-[55px] px-[20px] sm:px-[30px] pb-[80px]" style={{
        backgroundImage: 'radial-gradient(circle at 8% 72%, rgba(237, 228, 208, 0.32), transparent 24%), radial-gradient(circle at 90% 60%, rgba(239, 227, 207, 0.32), transparent 22%)'
      }}>
        <div className="w-full max-w-[1180px] mx-auto">
          {/* HERO / INTRO */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-[45px] lg:gap-[60px] pb-[65px]">
            <div className="max-w-[680px]">
              <div className="text-[11px] font-bold tracking-[0.18em] text-[#163f3a] uppercase mb-[18px] font-sans">
                {pageData?.hero_badge ? getLocalizedValue(pageData.hero_badge) : (lang === 'da' ? 'OM NORDISK PSYKIATRI' : 'OM NORDISK PSYKIATRI')}
              </div>

              <h1 className="font-display text-[42px] sm:text-[50px] lg:text-[62px] font-normal leading-[1.08] text-[#163f3a] m-0">
                {pageData?.hero_overskrift ? getLocalizedValue(pageData.hero_overskrift) : (lang === 'da' ? 'Om os og vores værdier' : 'Om oss och våra värderingar')}
              </h1>

              <div className="w-[38px] h-[1px] bg-[#6d9785] my-[24px] mb-[26px]" />

              {renderFormattedText(
                pageData?.hero_beskrivelse,
                lang === 'da' ? 'Mød specialisterne bag klinikken. Vi arbejder hver dag for at skabe et sammenhængende rum, hvor mennesket altid er i centrum.' : 'Möt specialisterna bakom kliniken. Vi arbetar varje dag för att skapa ett sammanhängande rum, där människan alltid är i centrum.',
                "text-[15px] leading-[1.7] text-[#344641] max-w-[630px] m-0 font-sans [&>p]:m-0 [&>p]:leading-[1.7] [&>p:not(:last-child)]:mb-3"
              )}

              <div className="mt-[55px] lg:mt-[80px]">
                <div className="text-[11px] font-bold tracking-[0.18em] text-[#163f3a] uppercase mb-[18px] font-sans">
                  {pageData?.team_badge ? getLocalizedValue(pageData.team_badge) : (lang === 'da' ? 'MENNESKENE BAG KLINIKKEN' : 'MÄNNISKORNA BAKOM KLINIKEN')}
                </div>

                <h2 className="font-display text-[34px] sm:text-[40px] lg:text-[48px] font-normal leading-[1.1] text-[#163f3a] m-0">
                  {pageData?.team_overskrift ? getLocalizedValue(pageData.team_overskrift) : (lang === 'da' ? 'Mød Martin og Felix' : 'Möt Martin och Felix')}
                </h2>

                <div className="w-[38px] h-[1px] bg-[#6d9785] my-[24px] mb-[26px]" />

                {renderFormattedText(
                  pageData?.team_beskrivelse,
                  lang === 'da' ? 'Vi har grundlagt Nordisk Psykiatri ud fra et fælles ønske om at skabe grundige og sammenhængende forløb, hvor psykiatrisk og psykologisk faglighed bringes tættere sammen.' : 'Vi grundade Nordisk Psykiatri utifrån en gemensam önskan om att skapa grundliga och sammanhängande förlopp, där psykiatrisk och psykologisk expertis förs närmare varandra.',
                  "text-[15px] leading-[1.7] text-[#344641] m-0 font-sans [&>p]:m-0 [&>p]:leading-[1.7] [&>p:not(:last-child)]:mb-3"
                )}
              </div>
            </div>

            {/* HERO DEKORATION */}
            <div className="relative min-h-[260px] sm:min-h-[300px] lg:min-h-[420px]">
              <div className="hidden lg:block absolute left-0 top-0 w-[1px] h-full bg-[#163f3a]/28" />
              <div 
                className="absolute w-[200px] h-[200px] sm:w-[230px] sm:h-[230px] lg:w-[260px] lg:h-[260px] rounded-full top-[0px] right-[35%] sm:right-[30%] lg:right-[20px]"
                style={{ backgroundColor: 'rgba(177, 190, 145, 0.38)' }} 
              />
              <div 
                className="absolute w-[200px] h-[200px] sm:w-[230px] sm:h-[230px] lg:w-[260px] lg:h-[260px] rounded-full top-[90px] sm:top-[110px] lg:top-[140px] right-[18%] sm:right-[15%] lg:right-[20px]"
                style={{ backgroundColor: 'rgba(139, 148, 214, 0.28)' }} 
              />
            </div>
          </div>
        </div>
      </section>

      {/* KLINISK PSYKOLOG - FELIX (Fuld bredde med baggrundsfarven #f6f0e2) */}
      <section 
        className="w-full py-[60px] lg:py-[75px] px-6 lg:px-12"
        style={{ backgroundColor: '#f6f0e2' }}
      >
        <div className="w-full max-w-[1180px] mx-auto grid grid-cols-1 md:grid-cols-[1.08fr_0.92fr] gap-[40px] lg:gap-[65px] items-center">
          <div>
            <div className="overflow-hidden rounded-[12px] shadow-xs">
              <img
                src={
                  pageData?.felix_billede?.[0]
                    ? (pageData.felix_billede[0].startsWith('http') && !pageData.felix_billede[0].includes('/cdn-cgi/image/')
                        ? pageData.felix_billede[0].replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/')
                        : pageData.felix_billede[0])
                    : "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/image_18cedeea.png"
                }
                alt={pageData?.felix_navn ? getLocalizedValue(pageData.felix_navn) : "Felix Enghoff Klich"}
                className="w-full h-[320px] sm:h-[420px] object-cover block object-top"
              />
            </div>
          </div>

          <div className="w-full max-w-[470px]">
            <div className="text-[11px] font-bold tracking-[0.18em] text-[#163f3a] uppercase mb-[18px] font-sans">
              {pageData?.felix_navn ? getLocalizedValue(pageData.felix_navn) : 'FELIX ENGHOFF KLICH'}
            </div>

            <h2 className="font-display text-[34px] sm:text-[40px] lg:text-[48px] font-normal leading-[1.08] text-[#163f3a] m-0">
              {pageData?.felix_titel ? getLocalizedValue(pageData.felix_titel) : (lang === 'da' ? 'Klinisk psykolog' : 'Klinisk psykolog')}
            </h2>

            <div className="w-[38px] h-[1px] bg-[#6d9785] my-[24px] mb-[26px]" />

            {renderFormattedText(
              pageData?.felix_beskrivelse,
              lang === 'da' ? 'Felix arbejder med psykologisk udredning og behandling af blandt andet ADHD, autisme, OCD, angst og andre psykiatriske problemstillinger. Han lægger vægt på grundighed, tydelighed og på at forstå det enkelte menneskes samlede livssituation.' : 'Felix arbetar med psykologisk utredning och behandling av bland annat ADHD, autism, OCD, ångest och andra psykiatriska frågeställningar. Han lägger stor vikt vid noggrannhet, tydlighet och att förstå den enskilda människans samlade livssituation.',
              "text-[15px] leading-[1.7] text-[#344641] max-w-[440px] m-0 font-sans [&>p]:m-0 [&>p]:leading-[1.7] [&>p:not(:last-child)]:mb-3"
            )}
          </div>
        </div>
      </section>

      {/* HOVEDLÆGE I PSYKIATRI - MARTIN (Fuld bredde) */}
      <section 
        className="w-full py-[60px] lg:py-[75px] px-6 lg:px-12"
        style={{ backgroundColor: '#f0eee7' }}
      >
        <div className="w-full max-w-[1180px] mx-auto grid grid-cols-1 md:grid-cols-[0.92fr_1.08fr] gap-[40px] lg:gap-[65px] items-center">
          <div className="w-full max-w-[470px] order-2 md:order-1">
            <div className="text-[11px] font-bold tracking-[0.18em] text-[#163f3a] uppercase mb-[18px] font-sans">
              {pageData?.martin_navn ? getLocalizedValue(pageData.martin_navn) : 'MARTIN BERGHOLTZ'}
            </div>

            <h2 className="font-display text-[34px] sm:text-[40px] lg:text-[48px] font-normal leading-[1.08] text-[#163f3a] m-0">
              {pageData?.martin_titel ? getLocalizedValue(pageData.martin_titel) : (lang === 'da' ? 'Hovedlæge i psykiatri' : 'Överläkare i psykiatri')}
            </h2>

            <div className="w-[38px] h-[1px] bg-[#6d9785] my-[24px] mb-[26px]" />

            {renderFormattedText(
              pageData?.martin_beskrivelse,
              lang === 'da' ? 'Martin er hovedlæge i psykiatri og befinder sig i den allerstørste del af sin speciallægeuddannelse i psykiatri. Han varetager psykiatrisk udredning og medicinsk behandling under supervision af psykiater og lægger vægt på en grundig, helhedsorienteret vurdering.' : 'Martin är läkare inom psykiatri och befinner sig i den sista delen av sin specialistutbildning i psykiatri. Han ansvarar för psykiatrisk utredning och medicinsk behandling under handledning av psykiatriker och lägger vikt vid en noggrann, helhetsorienterad bedömning.',
              "text-[15px] leading-[1.7] text-[#344641] max-w-[440px] m-0 font-sans [&>p]:m-0 [&>p]:leading-[1.7] [&>p:not(:last-child)]:mb-3"
            )}
          </div>

          <div className="order-1 md:order-2">
            <div className="overflow-hidden rounded-[12px] shadow-xs">
              <img
                src={
                  pageData?.martin_billede?.[0]
                    ? (pageData.martin_billede[0].startsWith('http') && !pageData.martin_billede[0].includes('/cdn-cgi/image/')
                        ? pageData.martin_billede[0].replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/')
                        : pageData.martin_billede[0])
                    : "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/martin.jpg"
                }
                alt={pageData?.martin_navn ? getLocalizedValue(pageData.martin_navn) : "Martin Bergholtz"}
                className="w-full h-[320px] sm:h-[420px] object-cover block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="pt-6 sm:pt-8 lg:pt-10 pb-16 lg:pb-24 bg-sand-light">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="max-w-[760px] mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-petrol-light mb-3">
              {pageData ? getLocalizedValue(pageData.vaerdier_badge) : (lang === 'da' ? 'Vores værdier' : 'Våra värderingar')}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight text-petrol-dark leading-[1.15]">
              {pageData ? getLocalizedValue(pageData.vaerdier_overskrift) : t('about.values_desc')}
            </h2>
            <div className="w-[34px] h-[1px] bg-petrol-dark my-6" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0">
            {/* Value 1 */}
            <article className="lg:px-6 lg:border-r lg:border-[#dfe1de] first:pl-0 last:border-0 text-center lg:text-left">
              <div className="w-[58px] h-[58px] rounded-full bg-[#f8f4ee] flex items-center justify-center mx-auto lg:mx-0 mb-4">
                <svg className="w-[30px] h-[30px] fill-none stroke-petrol-dark stroke-[1.5]" viewBox="0 0 24 24">
                  <path d="M12 3 4.5 6v5.5c0 4.8 3.1 8 7.5 9.5 4.4-1.5 7.5-4.7 7.5-9.5V6L12 3Z"/>
                  <path d="M12 6v12"/>
                </svg>
              </div>
              <h3 className="text-[13px] font-semibold uppercase tracking-[0.1em] text-petrol-dark mb-2">
                {pageData ? getLocalizedValue(pageData.vaerdi_1_titel) : t('about.val_1_title')}
              </h3>
              <p className="text-[13px] text-text-dark/80 leading-relaxed">
                {pageData ? getLocalizedValue(pageData.vaerdi_1_beskrivelse) : t('about.val_1_desc')}
              </p>
            </article>

            {/* Value 2 */}
            <article className="lg:px-6 lg:border-r lg:border-[#dfe1de] last:border-0 text-center lg:text-left">
              <div className="w-[58px] h-[58px] rounded-full bg-[#f8f4ee] flex items-center justify-center mx-auto lg:mx-0 mb-4">
                <svg className="w-[30px] h-[30px] fill-none stroke-petrol-dark stroke-[1.5]" viewBox="0 0 24 24">
                  <circle cx="10" cy="7" r="3"/>
                  <path d="M4 20v-2a6 6 0 0 1 9.4-4.9"/>
                  <path d="M17.5 12.5c2.5-2.2 5.6 1.5 0 5.7-5.6-4.2-2.5-7.9 0-5.7Z"/>
                </svg>
              </div>
              <h3 className="text-[13px] font-semibold uppercase tracking-[0.1em] text-petrol-dark mb-2">
                {pageData ? getLocalizedValue(pageData.vaerdi_2_titel) : t('about.val_2_title')}
              </h3>
              <p className="text-[13px] text-text-dark/80 leading-relaxed">
                {pageData ? getLocalizedValue(pageData.vaerdi_2_beskrivelse) : t('about.val_2_desc')}
              </p>
            </article>

            {/* Value 3 */}
            <article className="lg:px-6 lg:border-r lg:border-[#dfe1de] last:border-0 text-center lg:text-left">
              <div className="w-[58px] h-[58px] rounded-full bg-[#f8f4ee] flex items-center justify-center mx-auto lg:mx-0 mb-4">
                <svg className="w-[30px] h-[30px] fill-none stroke-petrol-dark stroke-[1.5]" viewBox="0 0 24 24">
                  <rect x="5" y="10" width="14" height="11" rx="2"/>
                  <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3"/>
                </svg>
              </div>
              <h3 className="text-[13px] font-semibold uppercase tracking-[0.1em] text-petrol-dark mb-2">
                {pageData ? getLocalizedValue(pageData.vaerdi_3_titel) : t('about.val_3_title')}
              </h3>
              <p className="text-[13px] text-text-dark/80 leading-relaxed">
                {pageData ? getLocalizedValue(pageData.vaerdi_3_beskrivelse) : t('about.val_3_desc')}
              </p>
            </article>

            {/* Value 4 */}
            <article className="lg:px-6 last:border-0 text-center lg:text-left">
              <div className="w-[58px] h-[58px] rounded-full bg-[#f8f4ee] flex items-center justify-center mx-auto lg:mx-0 mb-4">
                <svg className="w-[30px] h-[30px] fill-none stroke-petrol-dark stroke-[1.5]" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="6"/>
                  <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
                </svg>
              </div>
              <h3 className="text-[13px] font-semibold uppercase tracking-[0.1em] text-petrol-dark mb-2">
                {pageData ? getLocalizedValue(pageData.vaerdi_4_titel) : t('about.val_4_title')}
              </h3>
              <p className="text-[13px] text-text-dark/80 leading-relaxed">
                {pageData ? getLocalizedValue(pageData.vaerdi_4_beskrivelse) : t('about.val_4_desc')}
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* History & Mission Section */}
      <section 
        className="bg-[#CFE0DB] py-16 lg:py-24 border-y border-petrol-dark/10"
        style={{ backgroundColor: '#CFE0DB' }}
      >
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-0">
            {/* History Card */}
            <article className="lg:pr-12 lg:border-r lg:border-petrol-dark/15">
              <div className="w-[34px] h-[1px] bg-petrol-dark mb-6" />
              {pageData?.historie_tekst ? (
                <div 
                  className="space-y-4 text-sm text-text-dark/80 leading-relaxed [&>h3]:font-display [&>h3]:text-3xl [&>h3]:font-light [&>h3]:tracking-tight [&>h3]:text-petrol-dark [&>h3]:mb-6 [&>p]:mb-3"
                  dangerouslySetInnerHTML={{ __html: getLocalizedValue(pageData.historie_tekst) }}
                />
              ) : (
                <>
                  <h3 className="font-display text-3xl font-light tracking-tight text-petrol-dark mb-6">
                    {t('about.history_title')}
                  </h3>
                  <div className="space-y-4 text-sm text-text-dark/80 leading-relaxed">
                    <p>{t('about.history_desc_1')}</p>
                    <p>{t('about.history_desc_2')}</p>
                  </div>
                </>
              )}
            </article>

            {/* Mission Card */}
            <article className="lg:pl-16">
              <div className="w-[34px] h-[1px] bg-petrol-dark mb-6" />
              {pageData?.mission_tekst ? (
                <div 
                  className="space-y-4 text-sm text-text-dark/80 leading-relaxed [&>h3]:font-display [&>h3]:text-3xl [&>h3]:font-light [&>h3]:tracking-tight [&>h3]:text-petrol-dark [&>h3]:mb-6 [&>p]:mb-3"
                  dangerouslySetInnerHTML={{ __html: getLocalizedValue(pageData.mission_tekst) }}
                />
              ) : (
                <>
                  <h3 className="font-display text-3xl font-light tracking-tight text-petrol-dark mb-6">
                    {t('about.mission_title')}
                  </h3>
                  <div className="space-y-4 text-sm text-text-dark/80 leading-relaxed">
                    <p>{t('about.mission_desc_1')}</p>
                    <p>{t('about.mission_desc_2')}</p>
                  </div>
                </>
              )}
            </article>
          </div>
        </div>
      </section>

      {/* Quality & Patientsafety Section */}
      <section className="py-16 lg:py-24 bg-sand-light border-t border-[#dfe1de]/60">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="max-w-[760px] mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-petrol-light mb-3">
              {t('about.quality_eyebrow')}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight text-petrol-dark leading-[1.15]">
              {pageData ? getLocalizedValue(pageData.kvalitet_overskrift) : t('about.quality_title')}
            </h2>
            <div className="w-[34px] h-[1px] bg-petrol-dark my-6" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0">
            {/* Quality 1 */}
            <article className="lg:px-6 lg:border-r lg:border-[#dfe1de] first:pl-0 last:border-0">
              <svg className="w-[30px] h-[30px] fill-none stroke-petrol-dark stroke-[1.5] mb-4" viewBox="0 0 24 24">
                <path d="M5 21V7l7-4 7 4v14M3 21h18M9 10h2M13 10h2M9 14h2M13 14h2M11 21v-3h2v3"/>
              </svg>
              <p className="text-[13px] text-text-dark/80 leading-relaxed">
                {t('about.quality_1')}
              </p>
            </article>

            {/* Quality 2 */}
            <article className="lg:px-6 lg:border-r lg:border-[#dfe1de] last:border-0">
              <svg className="w-[30px] h-[30px] fill-none stroke-petrol-dark stroke-[1.5] mb-4" viewBox="0 0 24 24">
                <circle cx="12" cy="7" r="4"/>
                <path d="M5 21a7 7 0 0 1 14 0"/>
              </svg>
              <p className="text-[13px] text-text-dark/80 leading-relaxed mb-4">
                {t('about.quality_2')}
              </p>
              <div className="flex flex-col gap-2 pt-1">
                <a
                  href={pageData?.felix_autorisationslink || "https://autregweb.stps.dk/da/personal?id=58198c51-50c6-f011-919b-005056aeb01c"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#163f3a] hover:text-petrol-medium underline underline-offset-4 decoration-[#163f3a]/40 hover:decoration-petrol-medium transition-colors group w-fit"
                >
                  <span>{lang === 'da' ? 'Felix Klich · Autoriseret psykolog' : 'Felix Klich · Legitimerad psykolog'}</span>
                  <span className="text-[12px] leading-none transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">↗</span>
                </a>
                <a
                  href={pageData?.martin_autorisationslink || "https://autregweb.stps.dk/da/personal?id=53cbbcd4-2c52-ea11-9114-00505696bb68"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#163f3a] hover:text-petrol-medium underline underline-offset-4 decoration-[#163f3a]/40 hover:decoration-petrol-medium transition-colors group w-fit"
                >
                  <span>{lang === 'da' ? 'Martin Bergholtz · Læge' : 'Martin Bergholtz · Läkare'}</span>
                  <span className="text-[12px] leading-none transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">↗</span>
                </a>
              </div>
            </article>

            {/* Quality 3 */}
            <article className="lg:px-6 lg:border-r lg:border-[#dfe1de] last:border-0">
              <svg className="w-[30px] h-[30px] fill-none stroke-petrol-dark stroke-[1.5] mb-4" viewBox="0 0 24 24">
                <rect x="5" y="4" width="14" height="17" rx="2"/>
                <path d="M9 4V2h6v2M8 9h8M8 13h8M8 17h5"/>
              </svg>
              <p className="text-[13px] text-text-dark/80 leading-relaxed">
                {t('about.quality_3')}
              </p>
            </article>

            {/* Quality 4 */}
            <article className="lg:px-6 last:border-0">
              <svg className="w-[30px] h-[30px] fill-none stroke-petrol-dark stroke-[1.5] mb-4" viewBox="0 0 24 24">
                <rect x="6" y="10" width="12" height="10" rx="2"/>
                <path d="M9 10V7a3 3 0 0 1 6 0v3M12 14v2"/>
              </svg>
              <p className="text-[13px] text-text-dark/80 leading-relaxed">
                {t('about.quality_4')}
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="grid grid-cols-1 lg:grid-cols-[48%_52%] min-h-[260px] bg-[#253a35]">
        <div className="bg-[#253a35] flex flex-col justify-center px-6 py-12 md:py-16 lg:pl-[calc((100vw-1180px)/2)] lg:pr-12 xl:pl-[calc((100vw-1180px)/2)]">
          <div className="max-w-[455px] mx-auto lg:mx-0">
            <h3 className="font-display text-3xl font-light text-white mb-2">
              {pageData ? getLocalizedValue(pageData.cta_overskrift) : t('about.cta_title')}
            </h3>
            <p className="text-sm text-white/80 mb-6">
              {pageData ? getLocalizedValue(pageData.cta_beskrivelse) : t('about.cta_desc')}
            </p>
            <Link 
              to="/kontakt" 
              className="inline-flex items-center gap-3 bg-white text-petrol-dark text-sm font-semibold px-6 py-3 rounded-full hover:bg-sand-light transition-colors group"
            >
              {t('about.cta_btn')}
              <span className="text-lg transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </div>
        <div 
          className="min-h-[260px] lg:min-h-full bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url('${(pageData?.cta_billede && pageData.cta_billede[0]) ? pageData.cta_billede[0] : 'https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/image_ff15c013.png'}')` 
          }}
        />
      </section>
    </div>
  );
}
